import { AlertTriangle } from 'lucide-react';
import { Component, type ReactElement, type ReactNode } from 'react';

import { logError } from '../services/errorTracking';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  errorMessage: string | null;
}

function ErrorFallback(): ReactElement {
  const handleReturnHome = (): void => {
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen bg-theme-bg flex items-center justify-center">
      <div className="text-center max-w-md px-6">
        <AlertTriangle className="w-12 h-12 text-theme-warning mx-auto mb-4" />
        <h1 className="text-xl font-bold text-theme-text mb-2">Something went wrong</h1>
        <p className="text-theme-text-secondary mb-6">
          An unexpected error occurred. Please try again.
        </p>
        <button
          type="button"
          onClick={handleReturnHome}
          className="px-6 py-2.5 bg-theme-primary-fill text-theme-on-primary-fill font-medium rounded-lg hover:opacity-90 transition-opacity"
        >
          Return Home
        </button>
      </div>
    </div>
  );
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, errorMessage: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, errorMessage: error.message };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    logError(error, { componentStack: errorInfo.componentStack });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return <ErrorFallback />;
    }
    return this.props.children;
  }
}
